import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { getNotificationTypes } from "../api/notifications-api";
import type { NotificationScopeSelection, NotificationTypeResource } from "../types/Notifications.type";

export const NOTIFICATION_TYPES_KEY = {
	all: ["notifications", "types"] as const,
	byScope: (scope: NotificationScopeSelection) =>
		[...NOTIFICATION_TYPES_KEY.all, scope.scopeType, scope.scopeId ?? null] as const,
};

// A non-global scope is INCOMPLETE without a numeric scopeId (e.g. the RFx
// scope's text input hasn't resolved to a valid id yet, or the scope type
// just changed and scopeId was reset). The BE's resolveScopeContext 403s a
// non-global scope_type with a missing/non-numeric scope_id by design (no
// existence leak) — that's correct backend behaviour, but the FE must not
// fire the request at all until the scope is actually complete, or every
// intermediate keystroke throws a spurious "unauthorized" toast.
const isCompleteScope = (scope: NotificationScopeSelection): boolean =>
	scope.scopeType === "global" ||
	(scope.scopeId !== undefined && Number.isFinite(scope.scopeId));

export const useNotificationTypes = (
	scope: NotificationScopeSelection
): UseQueryResult<NotificationTypeResource[]> => {
	return useQuery({
		queryKey: NOTIFICATION_TYPES_KEY.byScope(scope),
		queryFn: () => getNotificationTypes(scope),
		enabled: isCompleteScope(scope),
	});
};
